import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {GridColDef} from '@mui/x-data-grid';
import {EntioDataGrid} from 'src/components/ui';
import {
  acsIntegrationClient,
  PdkSyncReason,
  PdkSyncStatus,
  PdkSyncStatusReport,
  PdkSyncStatusRow,
} from 'src/apis/acsIntegrationClient';
import {formatDate} from 'src/utils/common';

type Props = {
  open: boolean;
  integrationId: string | null;
  onClose: () => void;
};

type StatusFilter = 'all' | PdkSyncStatus;

const STATUS_META: Record<
  PdkSyncStatus,
  {label: string; color: 'success' | 'info' | 'warning' | 'default'; help: string}
> = {
  synced: {
    label: 'In Entrio',
    color: 'success',
    help: 'Linked to an Entrio resident and kept in sync.',
  },
  ready: {
    label: 'Ready to create',
    color: 'info',
    help: 'Not in Entrio yet, but nothing is missing. Created on the next PDK update for this person, or from the enable review.',
  },
  not_imported: {
    label: 'Not imported',
    color: 'warning',
    help: 'Auto-sync skips these people until the reason is fixed in PDK or Entrio.',
  },
  entrio_only: {
    label: 'Only in Entrio',
    color: 'default',
    help: 'Linked residents PDK no longer has.',
  },
};

function reasonText(row: PdkSyncStatusRow): string {
  const r: PdkSyncReason | null = row.reason;
  switch (r) {
    case 'no_email':
      return 'No email in PDK';
    case 'no_unit':
      return 'No unit in PDK';
    case 'unit_unmatched':
      return `No Entrio room named “${row.pdkUnitName || row.pdkUnits[0] || '?'}”`;
    case 'unit_ambiguous':
      return row.pdkUnits.length > 1
        ? `PDK lists ${row.pdkUnits.length} units (${row.pdkUnits.join(', ')})`
        : `More than one Entrio room is named “${row.pdkUnitName || ''}”`;
    case 'unit_unknown':
      return 'PDK details failed to load';
    case 'unit_matched':
      return 'Unit matches one Entrio room';
    case 'email_in_entrio':
      return 'Email already in Entrio — links on next PDK update';
    case 'not_in_pdk':
      return 'Removed from PDK';
    default:
      return '';
  }
}

function personName(row: PdkSyncStatusRow) {
  return [row.firstName, row.lastName].filter(Boolean).join(' ').trim() || row.email || '—';
}

function entrioLocation(row: PdkSyncStatusRow) {
  if (!row.entrio) return '';
  return [row.entrio.apartmentName, row.entrio.buildingName, row.entrio.roomName]
    .filter(Boolean)
    .join(' / ');
}

export default function PdkSyncStatusModal({open, integrationId, onClose}: Props) {
  const [report, setReport] = useState<PdkSyncStatusReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await acsIntegrationClient.getPdkSyncStatus(integrationId);
      setReport(res.data.data);
    } catch (e: unknown) {
      const err = e as {response?: {data?: {message?: string}}};
      setError(err?.response?.data?.message || 'Failed to load sync status from PDK');
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    if (open) {
      setFilter('all');
      setQuery('');
      void load();
    }
  }, [open, load]);

  const rows = useMemo(() => {
    const people = report?.people || [];
    const q = query.trim().toLowerCase();
    return people.filter(row => {
      if (filter !== 'all' && row.status !== filter) return false;
      if (!q) return true;
      return [personName(row), row.email, row.phone, row.pdkUnitName, entrioLocation(row)]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q));
    });
  }, [report, filter, query]);

  const columns: GridColDef<PdkSyncStatusRow>[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 170,
      valueGetter: ({row}) => personName(row),
      renderCell: ({row}) => (
        <Box sx={{py: 0.5, minWidth: 0}}>
          <Typography variant="body2" noWrap>
            {personName(row)}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {row.email || 'No email'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140,
      valueGetter: ({row}) => row.phone || '',
      renderCell: ({row}) =>
        row.phone ? (
          row.phone
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        ),
    },
    {
      field: 'pdkUnit',
      headerName: 'PDK unit',
      width: 120,
      valueGetter: ({row}) => row.pdkUnitName || row.pdkUnits.join(', '),
    },
    {
      field: 'entrio',
      headerName: 'Entrio location',
      flex: 1.2,
      minWidth: 180,
      valueGetter: ({row}) => entrioLocation(row),
      renderCell: ({row}) =>
        row.entrio ? (
          entrioLocation(row) || '—'
        ) : (
          <Typography variant="caption" color="text.secondary">
            Not in Entrio
          </Typography>
        ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1.3,
      minWidth: 220,
      valueGetter: ({row}) => STATUS_META[row.status].label,
      renderCell: ({row}) => {
        const meta = STATUS_META[row.status];
        const why = reasonText(row);
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 0}}>
            <Chip size="small" color={meta.color} variant="outlined" label={meta.label} />
            {why && row.status !== 'synced' && (
              <Tooltip title={row.loadError || why}>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {why}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'lastSyncedAt',
      headerName: 'Last synced',
      width: 150,
      valueGetter: ({row}) => row.lastSyncedAt || '',
      renderCell: ({row}) => (row.lastSyncedAt ? formatDate(row.lastSyncedAt) : '—'),
    },
  ];

  const filters: Array<[StatusFilter, string]> = [
    ['all', 'All'],
    ['synced', STATUS_META.synced.label],
    ['ready', STATUS_META.ready.label],
    ['not_imported', STATUS_META.not_imported.label],
    ['entrio_only', STATUS_META.entrio_only.label],
  ];

  const countFor = (f: StatusFilter) =>
    f === 'all' ? report?.people.length || 0 : report?.counts[f] || 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            PDK people sync status
            <Typography variant="body2" color="text.secondary">
              Who from PDK is in Entrio, and why the rest are not.
              {report ? ` Checked ${formatDate(report.generatedAt)}.` : ''}
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
            onClick={() => void load()}
            disabled={loading || !integrationId}
          >
            Refresh
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{mb: 1.5}}>
            {error}
          </Alert>
        )}
        {report && !report.autoSyncEnabled && (
          <Alert severity="info" sx={{mb: 1.5}}>
            Auto-sync is off. Nothing changes in Entrio until you turn it on or run a manual import.
          </Alert>
        )}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{mb: 1.5, flexWrap: 'wrap', rowGap: 1}}
        >
          {filters.map(([id, label]) => (
            <Tooltip key={id} title={id === 'all' ? '' : STATUS_META[id].help}>
              <Button
                size="small"
                variant={filter === id ? 'contained' : 'outlined'}
                onClick={() => setFilter(id)}
                disabled={loading && !report}
              >
                {label} ({countFor(id)})
              </Button>
            </Tooltip>
          ))}
          <Box sx={{flex: 1}} />
          <TextField
            size="small"
            placeholder="Search name, email, phone, unit"
            value={query}
            onChange={e => setQuery(e.target.value)}
            sx={{minWidth: 260}}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        {filter !== 'all' && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{mb: 1}}>
            {STATUS_META[filter].help}
          </Typography>
        )}
        <EntioDataGrid
          loading={loading}
          rows={rows}
          getRowId={r => `${r.status}:${r.holderId}`}
          columns={columns}
          pageSizeOptions={[10, 25, 50]}
          emptyMessage={loading ? 'Loading from PDK…' : 'No people'}
          emptyDescription={
            loading ? 'This reads every PDK person, so it can take a little while.' : undefined
          }
          sx={{minWidth: 900}}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
